import React from 'react';
import { Helmet } from 'react-helmet';
import { SocialMedia, Brand } from './store/selectors';
import { Article, Breadcrumb } from '../../models/article';
import uniq from 'lodash/uniq';
const { isArray } = Array;

interface MetadataProps extends Article {
  brand: Brand;
  socialMedia: SocialMedia;
  faviconUrl: string;
  amp?: boolean;
  urlBreadcrumbs: Breadcrumb[];
}

export const produceKeywords = (
  tags: MetadataProps['tags'],
  categories: MetadataProps['categories']
) => {
  const cleanedTags = isArray(tags) ? tags.map(name => name.toLowerCase()) : [];
  const cleanedCategories = isArray(categories)
    ? categories.map(({ name }) => name.toLowerCase())
    : [];
  return uniq(cleanedCategories.concat(cleanedTags));
};

const Metadata = ({
  categories,
  title,
  facebook_title,
  urlBreadcrumbs,
  seo_description,
  excerpt,
  thumbnail_image,
  medium_image,
  large_image,
  full_image,
  seo_canonical_url,
  created,
  modified,
  link,
  socialMedia,
  faviconUrl,
  section,
  author,
  amp,
  tags,
}: MetadataProps) => (
  <Helmet>
    <meta name={'robots'} content={'noodp,noydir'} />
    <meta
      name={'keywords'}
      content={
        isArray(categories) ? categories.map(({ name }) => name).join(',') : ''
      }
    />
    <meta
      property={'og:title'}
      content={facebook_title || title || socialMedia.site}
    />
    <meta
      name={'twitter:title'}
      content={facebook_title || title || socialMedia.site}
    />

    <meta name={'description'} content={seo_description || excerpt} />
    <meta property={'og:description'} content={seo_description || excerpt} />
    <meta name={'twitter:description'} content={seo_description || excerpt} />

    <meta property={'fb:app_id'} content={socialMedia.fb_id} />
    <meta property={'og:site_name'} content={socialMedia.site} />

    <meta property={'og:image'} content={large_image} />
    <meta property={'og:image:url'} content={large_image} />
    <meta property={'og:image:secure_url'} content={large_image} />
    <meta name={'twitter:image'} content={large_image} />
    <meta name={'twitter:image:width'} content={'770'} />
    <meta name={'twitter:image:height'} content={'405'} />

    <meta property={'og:url'} content={seo_canonical_url || link} />
    <meta property={'og:type'} content={'article'} />
    <meta property={'og:locale'} content={'en_US'} />
    <meta property={'og:updated_time'} content={modified} />
    <meta
      property={'article:publisher'}
      content={`https://www.facebook.com/${socialMedia.facebook}/`}
    />
    <meta
      property={'article:author'}
      content={`https://www.facebook.com/${socialMedia.facebook}/`}
    />
    <meta property={'article:published_time'} content={created} />
    <meta property={'article:modified_time'} content={modified} />

    <meta name={'twitter:card'} content={'summary_large_image'} />
    <meta name={'twitter:site'} content={socialMedia.twitter} />

    <link rel={'canonical'} href={seo_canonical_url || link} />
    <link rel={'icon'} type={'image/png'} href={faviconUrl} />

    {amp && <link rel={'amphtml'} href={`${seo_canonical_url || link}/amp`} />}
    {urlBreadcrumbs && !!urlBreadcrumbs.length && (
      <script type={'application/ld+json'}>
        {JSON.stringify({
          '@context': 'http://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: urlBreadcrumbs.map((breadcrumb, index) => {
            return {
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@id': breadcrumb.link,
                name: breadcrumb.name,
              },
            };
          }),
        })}
      </script>
    )}

    <script type={'application/ld+json'}>
      {JSON.stringify({
        '@context': 'http://schema.org',
        '@type': 'Article',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': seo_canonical_url || link,
        },
        headline: title,
        url: seo_canonical_url || link,
        description: seo_description || excerpt,
        thumbnailUrl: thumbnail_image,
        image: [full_image, large_image, medium_image, thumbnail_image],
        dateCreated: created,
        dateModified: modified,
        datePublished: created,
        articleSection: section,
        author: {
          '@type': 'Person',
          name: author.name,
        },
        creator: [author.name],
        keywords: produceKeywords(tags, categories),
        publisher: {
          '@type': 'Organization',
          name: socialMedia.site,
          logo: {
            '@type': 'ImageObject',
            url: 'https://i.startsatsixty.com.au/wp-content/uploads/20170616031142/19105610_1630986553602027_6878155112111356803_n-100x100.png',
          },
        },
      })}
    </script>
  </Helmet>
);

export default Metadata;
